import { useSharedAPI } from "@certn/api";
import { paths } from "@certn/api/generated-schemas/shared";
import { FocusEvent, useEffect, useMemo, useRef, useState } from "react";
import { useDebounce } from "usehooks-ts";

import { SearchableSelect } from "../../components/inputs/select/SearchableSelect";
import { SelectItem } from "../../components/inputs/select/SelectItem";
import { FieldErrorsProps } from "../../components/inputs/_assets/input-wrapper/FieldErrors";

type SharedGetPath = keyof {
  [K in keyof paths as paths[K] extends { get: unknown } ? K : never]: paths[K];
};

export type CompanyLookupSelectProps = {
  label: string;
  isRequired?: boolean;
  testID: string;
  lookupApiPath: SharedGetPath;
  lookupQueryParam?: string;
  lookupItemKey?: string;
  lookupItemLabel?: string;
  minSearchLength?: number;
  initialInputValue?: string;
  onDebouncedInputChange?: (value: string) => void;
  onSelectionChange?: (itemKey: string, itemLabel: string) => void;
  errors?: FieldErrorsProps["children"];
  isInvalid?: boolean;
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  isDisabled?: boolean;
  description?: string;
  info?: React.ReactNode;
};

export function CompanyLookupSelect(props: CompanyLookupSelectProps) {
  const {
    label,
    isRequired,
    testID,
    lookupApiPath,
    lookupQueryParam = "q",
    lookupItemKey = "reference_number",
    lookupItemLabel = "name",
    minSearchLength = 2,
    initialInputValue = "",
    onDebouncedInputChange,
    onSelectionChange,
    description,
    info,
  } = props;

  const [inputValue, setInputValue] = useState(initialInputValue);
  const isTypingRef = useRef(false);
  const hasUserEditedRef = useRef(false);

  useEffect(() => {
    if (!isTypingRef.current && initialInputValue !== inputValue) {
      setInputValue(initialInputValue);
    }
  }, [initialInputValue, inputValue]);

  const debouncedQuery = useDebounce(inputValue, 300);
  const shouldSearch = debouncedQuery.trim().length >= minSearchLength;

  const lookupQuery = useMemo(
    () => ({ [lookupQueryParam]: debouncedQuery.trim() }),
    [lookupQueryParam, debouncedQuery]
  );

  const { data: lookupList, isLoading } = useSharedAPI(
    shouldSearch ? lookupApiPath : null,
    lookupQuery,
    { keepPreviousData: true }
  );

  const items = useMemo(() => {
    const rows = (lookupList ?? []) as Record<string, string>[];
    return rows
      .filter((row) => row[lookupItemKey] && row[lookupItemLabel])
      .map((row) => ({
        key: String(row[lookupItemKey]),
        label: String(row[lookupItemLabel]),
      }));
  }, [lookupList, lookupItemKey, lookupItemLabel]);

  useEffect(() => {
    if (!hasUserEditedRef.current) {
      return;
    }
    onDebouncedInputChange?.(debouncedQuery);
  }, [debouncedQuery, onDebouncedInputChange]);

  return (
    <SearchableSelect
      isInvalid={props.isInvalid}
      errors={props.errors}
      menuTrigger="focus"
      testID={testID}
      data-testid={`${testID}-wrapper`}
      label={label}
      isRequired={isRequired}
      isDisabled={props.isDisabled}
      allowsCustomValue
      allowSearchWhenEmpty
      defaultFilter={() => true}
      showLoadingInInput={false}
      inputValue={inputValue}
      onInputChange={(value) => {
        isTypingRef.current = true;
        hasUserEditedRef.current = true;
        setInputValue(value);
      }}
      onFocusChange={(isFocused) => {
        if (!isFocused) {
          isTypingRef.current = false;
        }
      }}
      onSelectionChange={(key) => {
        if (!key) {
          return;
        }
        setTimeout(() => {
          isTypingRef.current = false;
          const match = items.find((item) => item.key === key);
          if (match) {
            setInputValue(match.label);
            onSelectionChange?.(match.key, match.label);
          }
        });
      }}
      isLoading={isLoading}
      items={items}
      onBlur={props.onBlur}
      description={description}
      info={info}
    >
      {(item) => (
        <SelectItem key={item.key} id={item.key} textValue={item.label}>
          {item.label}
        </SelectItem>
      )}
    </SearchableSelect>
  );
}
